"use client";

import { useLocale, useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { values } from "@/data/main-data";
import { useGetAbout } from "@/hooks/main-hooks";
import { BadgeCheck } from "lucide-react";

export default function Values() {
  const t = useTranslations("AboutPage");
  const locale = useLocale();
  const isAr = locale === "ar";

  const { data: about } = useGetAbout();

  // Use dashboard values when set, otherwise the static defaults (with icons).
  const items =
    about?.values?.length
      ? about.values.map((v: any, i: number) => ({
          title_ar: v.titleAr,
          title_en: v.titleEn,
          des_ar: v.descriptionAr,
          des_en: v.descriptionEn,
          icon: values[i % values.length]?.icon || BadgeCheck,
        }))
      : values;

  return (
    <section className="relative py-24 bg-white overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-40">
        <div className="absolute top-[10%] left-[5%] w-64 h-64 bg-brand-blue/5 blur-[100px] rounded-full" />
        <div className="absolute bottom-[10%] right-[5%] w-64 h-64 bg-brand-red/5 blur-[100px] rounded-full" />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div
          className={`mb-16 flex flex-col ${
            isAr ? "items-end text-right" : "items-start text-left"
          }`}
        >
          <motion.h2
            initial={{ opacity: 0, x: isAr ? 30 : -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-black text-brand-navy tracking-tighter uppercase"
          >
            {t("values")}
          </motion.h2>
          <div className="h-1.5 w-20 bg-brand-blue mt-4 rounded-full" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((item: any, idx: number) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className="group relative bg-gray-50/50 p-8 rounded-[2.5rem] border border-gray-100 hover:bg-white hover:shadow-2xl hover:shadow-brand-navy/5 transition-all duration-500 cursor-pointer overflow-hidden"
            >
              <span className="absolute -top-4 -right-2 text-9xl font-black text-brand-navy/3 group-hover:text-brand-navy/6 transition-colors select-none pointer-events-none italic">
                {idx + 1}
              </span>

              <div className="relative z-10">
                <div className="w-20 h-20 rounded-2xl bg-white shadow-inner flex items-center justify-center mb-8 group-hover:bg-brand-navy transition-colors duration-500">
                  <item.icon className="text-4xl text-brand-blue group-hover:text-white transition-colors duration-500" />
                </div>

                {/* النصوص */}
                <h3 className="text-2xl font-black text-brand-navy mb-4 group-hover:text-brand-blue transition-colors">
                  {locale === "ar" ? item.title_ar : item.title_en}
                </h3>

                <p className="text-gray-500 text-base leading-relaxed font-medium">
                  {locale === "ar" ? item.des_ar : item.des_en}
                </p>
              </div>

              <div className="absolute bottom-0 left-0 w-0 h-1.5 bg-brand-blue group-hover:w-full transition-all duration-700" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
